import React, { memo } from "react";
import { ContainerPrintableSacola } from "./styles";
import {
  phoneMask,
} from "../../services/Functions";
import { cepMask, cnpjMask, cpfMask } from "../../services/Masks";
import moment from "moment";
import { CestaAbandonada } from "../../types/CestaAbandonada";
import TableProdutos from "../TableProdutos/TableProdutos";
import { OrdenacaoConfig } from "../TableProdutos/OrdenacaoButton/ordenacaoUtils";
import {
  LayoutImpressaoSacola,
  defaultLayoutSacola,
} from "../../types/LayoutImpressao";

interface PrintableSacolaProps {
  sacola: CestaAbandonada | undefined;
  layoutConfig?: LayoutImpressaoSacola;
  ordenacao?: OrdenacaoConfig;
}

const PrintableSacola: React.ForwardRefRenderFunction<
  HTMLDivElement,
  PrintableSacolaProps
> = ({ sacola, layoutConfig = defaultLayoutSacola, ordenacao }, ref) => {
  const config = layoutConfig;

  const obterInformacoesContato = (cliente) => {
    return [
      config.telefone && cliente.telefone && phoneMask(cliente.telefone),
      config.email && cliente.email,
      config.dataNascimento && cliente.data_nascimento &&
        `data de nascimento: ${moment(cliente.data_nascimento).format("DD/MM/YYYY")}`,
    ].filter(Boolean).join(", ");
  };

  const obterInformacoesFiscais = (cliente) => {
    return [
      config.cpf && cliente.cpf && `CPF: ${cpfMask(cliente.cpf)}`,
      config.cnpj && cliente.cnpj && `CNPJ: ${cnpjMask(cliente.cnpj)}`,
      config.inscricaoEstadual && cliente.inscricao_estadual && `IE: ${cpfMask(cliente.inscricao_estadual)}`,
    ].filter(Boolean).join(", ");
  };

  const obterEnderecoCliente = (cliente) => {
    if (!config.endereco) return "";
    return [
      `${cliente.endereco || ""} ${cliente.numero || ""}`.trim(),
      cliente.complemento,
      cliente.bairro,
      cliente.cidade && cliente.estado && `${cliente.cidade} / ${cliente.estado}`,
      cliente.cep && cepMask(cliente.cep),
    ].filter(Boolean).join(", ");
  };

  const montarInformacoesCliente = () => {
    if (sacola && sacola.cliente) {
      const { cliente } = sacola;
      const contato = obterInformacoesContato(cliente);
      const fiscais = obterInformacoesFiscais(cliente);
      const endereco = obterEnderecoCliente(cliente);

      return (
        <>
          {contato && <span>{contato}<br /></span>}
          {fiscais && <span>{fiscais}<br /></span>}
          {endereco && <span>{endereco}<br /></span>}
        </>
      );
    }
    return null;
  };

  const calcularValorTotal = () => {
    if (!sacola) return 0;
    let total = sacola.valor_total || 0; // já em reais
    if (sacola.desconto_progressivo?.valor) {
      total -= sacola.desconto_progressivo.valor;
    }
    if (sacola.cupom) {
      if (sacola.cupom.tipoDesconto === 'porcentagem') {
        total = total - (total * sacola.cupom.desconto / 10000);
      } else {
        total = total - (sacola.cupom.desconto / 100); // centavos para reais
      }
    }
    if (sacola.frete?.preco) {
      total += sacola.frete.preco / 100; // centavos para reais
    }
    return total;
  };

  const montarInformacoesSacola = () => {
    if (sacola) {
      const informacoes: string[] = [];

      if (config.descontoProgressivo && sacola.desconto_progressivo?.valor && sacola.desconto_progressivo.valor > 0) {
        informacoes.push(`Desconto progressivo: -${sacola.desconto_progressivo.percentual}% (${sacola.moeda} ${(sacola.desconto_progressivo.valor).toFixed(2).replace(".", ",")})`);
      }

      if (sacola.cupom) {
        const descontoTexto = sacola.cupom.tipoDesconto === 'porcentagem'
          ? `${sacola.cupom.desconto / 100}%`
          : `${sacola.moeda} ${(sacola.cupom.desconto / 100).toFixed(2).replace(".", ",")}`;
        informacoes.push(`Cupom: ${sacola.cupom.cupom} (${descontoTexto})`);
      }

      if (sacola.frete?.preco !== undefined) {
        informacoes.push(`Frete: ${sacola.moeda} ${(sacola.frete.preco / 100).toFixed(2).replace(".", ",")} - ${sacola.frete.descricao || ''}`);
      }

      return informacoes.length > 0 ? informacoes.join(" | ") : null;
    }
    return null;
  };

  const temInfoHeader = config.numeroSacola || config.data || config.catalogo || config.vendedor;
  const temInfoCliente = montarInformacoesCliente();
  const temInfoValor = config.valorTotal;

  return (
    <>
      {sacola && (
        <ContainerPrintableSacola ref={ref}>
          {temInfoHeader && (
            <>
              <div className="container">
                <div className="flex">
                  {config.numeroSacola && (
                    <div className="titulo">Sacola #{sacola.id_cesta_abandonada}</div>
                  )}
                  {config.data && (
                    <div>
                      Data:{" "}
                      {moment(sacola.data_modificacao).format("DD/MM/YYYY HH:mm:ss")}
                    </div>
                  )}
                </div>
                {(config.catalogo || config.vendedor) && (
                  <div className="flex">
                    {[
                      config.catalogo && `Catálogo: ${sacola.catalogo!.descricao}`,
                      config.vendedor && `Vendedor: ${sacola.vendedor}`,
                    ].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
              <div className="container-linha">
                <div className="linha"></div>
              </div>
            </>
          )}
          {temInfoCliente && (
            <>
              <div className="container">
                <div className="flex">
                  <div>
                    <span className="boldInicial">{sacola.cliente!.nome} - </span>
                    {temInfoCliente}
                  </div>
                </div>
              </div>
              <div className="container-linha">
                <div className="linha"></div>
              </div>
            </>
          )}
          {temInfoValor && (
            <>
              <div className="container">
                <div className="flex">
                  <div>
                    {montarInformacoesSacola() && (
                      <>
                        <span>{montarInformacoesSacola()}</span>
                        <br />
                      </>
                    )}
                    <span>
                      Valor total da sacola:{" "}
                      {sacola?.moeda} {calcularValorTotal().toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="container-linha">
                <div className="linha"></div>
              </div>
            </>
          )}
          <div className="container">
            <TableProdutos
              produtos={sacola.produtos}
              moeda={sacola.moeda}
              template="impressao"
              layoutConfig={config.tabelaProdutos}
              ordenacaoExterna={ordenacao}
            />
          </div>
          {config.observacaoCliente && sacola.observacao && (
            <>
              <div className="container-linha" />
              <div className="container">
                <div className="flex">
                  <div>
                    <span>Observação do pedido: </span> <br />
                    {sacola.observacao}
                  </div>
                </div>
              </div>
            </>
          )}
        </ContainerPrintableSacola>
      )}
    </>
  );
};

export default memo(React.forwardRef(PrintableSacola));
